import React, { useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";

/** Admin-only, double-confirmed wipe of operational data (irreversible). */
export function PurgePanel() {
  const { t } = useLang();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [keepUsers, setKeepUsers] = useState(true);
  const [word, setWord] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setStep(0); setWord(""); setEmail(""); };

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/purge-data", { confirm: "SUPPRIMER", keep_users: keepUsers });
      setResult(res.data.deleted);
      toast.success(t("Données supprimées."));
      reset();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const total = result ? Object.values(result).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="bg-white border border-red-200 rounded-md shadow-sm p-5 space-y-4" data-testid="purge-panel">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{t("Vider les données")}</h3>
          <p className="text-xs text-slate-500 mt-1">
            {t("Supprime définitivement toutes les saisies, rapports, actions, photos et journaux. Les référentiels (géographie, listes) et votre compte administrateur sont conservés. Cette opération est irréversible : exportez d'abord ce que vous souhaitez garder.")}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Switch checked={keepUsers} onCheckedChange={setKeepUsers} data-testid="purge-keep-users" />
        <Label className="text-sm">{t(keepUsers ? "Conserver les comptes utilisateurs" : "Supprimer aussi tous les comptes (sauf le vôtre)")}</Label>
      </div>

      <Button variant="destructive" onClick={() => setStep(1)} data-testid="purge-open">
        <Trash2 className="h-4 w-4 mr-1.5" />{t("Vider les données…")}
      </Button>

      {result && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="purge-result">
          <b>{total}</b> {t("enregistrement(s) supprimé(s).")} {Object.entries(result).filter(([, n]) => n).map(([k, n]) => `${k}: ${n}`).join(" · ")}
        </div>
      )}

      <Dialog open={step > 0} onOpenChange={(o) => !o && reset()}>
        <DialogContent className="max-w-md" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">{t(step === 1 ? "Étape 1 sur 2 — Confirmer" : "Étape 2 sur 2 — Dernière vérification")}</DialogTitle>
            <DialogDescription>
              {t(step === 1
                ? "Tapez le mot SUPPRIMER en majuscules pour continuer."
                : "Saisissez votre adresse e-mail administrateur pour lancer la suppression définitive.")}
            </DialogDescription>
          </DialogHeader>
          {step === 1 ? (
            <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder="SUPPRIMER" autoFocus data-testid="purge-word" />
          ) : (
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={user?.email} autoFocus data-testid="purge-email" />
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={reset} data-testid="purge-cancel">{t("Annuler")}</Button>
            {step === 1 ? (
              <Button variant="destructive" disabled={word !== "SUPPRIMER"} onClick={() => setStep(2)} data-testid="purge-next">{t("Continuer")}</Button>
            ) : (
              <Button variant="destructive" disabled={busy || email.trim().toLowerCase() !== (user?.email || "").toLowerCase()} onClick={run} data-testid="purge-confirm">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}{t("Supprimer définitivement")}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PurgePanel;
